import { useEffect } from 'react'

interface SEOMeta {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'profile' | 'article'
  noIndex?: boolean
}

const DEFAULT_TITLE = 'MeuDriver — Aulas de Direção Particulares'
const DEFAULT_DESCRIPTION =
  'Encontre instrutores de direção particulares na sua cidade. Agende aulas online com segurança e praticidade.'
const DEFAULT_IMAGE = '/og-default.png'

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property'
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

export function useSEO({
  title,
  description,
  image,
  url,
  type = 'website',
  noIndex = false,
}: SEOMeta = {}) {
  useEffect(() => {
    const resolvedTitle = title ? `${title} — MeuDriver` : DEFAULT_TITLE
    const resolvedDesc = description ?? DEFAULT_DESCRIPTION
    const resolvedImage = image ?? DEFAULT_IMAGE
    const resolvedUrl = url ?? window.location.href

    // Page title
    document.title = resolvedTitle

    // Standard meta
    setMeta('description', resolvedDesc, true)
    if (noIndex) setMeta('robots', 'noindex,nofollow', true)

    // Open Graph
    setMeta('og:title', resolvedTitle)
    setMeta('og:description', resolvedDesc)
    setMeta('og:image', resolvedImage)
    setMeta('og:url', resolvedUrl)
    setMeta('og:type', type)
    setMeta('og:site_name', 'MeuDriver')
    setMeta('og:locale', 'pt_BR')

    // Twitter Card
    setMeta('twitter:card', resolvedImage !== DEFAULT_IMAGE ? 'summary_large_image' : 'summary', true)
    setMeta('twitter:title', resolvedTitle, true)
    setMeta('twitter:description', resolvedDesc, true)
    setMeta('twitter:image', resolvedImage, true)

    // Canonical link
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', resolvedUrl)

    return () => {
      // Restore defaults on unmount so navigating away doesn't leave stale tags
      document.title = DEFAULT_TITLE
    }
  }, [title, description, image, url, type, noIndex])
}
